package dev.kuml.portal.web.usage

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.html.Div
import io.kvision.html.div
import kotlinx.browser.window
import kotlinx.coroutines.launch

private const val COMPACT_BREAKPOINT_QUERY = "(max-width: 480px)"

/** @property element the chip widget itself. @property refresh re-fetches `GET /api/account/usage`
 * and updates the chip -- called right after a successful diagram generation so the "Nutzungs-
 * Indikator zaehlt sichtbar hoch" (design spec) without waiting for the next screen navigation. */
public class UsageChip(
    public val element: Div,
    public val refresh: () -> Unit,
)

/**
 * The global "Nutzungs-/Plan-Indikator" chip (design spec, section 4) -- dezent, informational
 * only, NEVER an upgrade CTA. The full text is always set as the `title` attribute (native browser
 * tooltip), and the visible text switches between [UsageFormat.full] and [UsageFormat.compact] at
 * the same narrow-viewport breakpoint the design calls for. Loading is silent-fail: this chip is
 * purely informational, so a network hiccup just leaves it blank rather than showing an alarming
 * error in the header.
 */
public fun Container.usageChip(api: PortalApi): UsageChip {
    val chip = div(className = "kportal-usage-chip") { setAttribute("aria-live", "polite") }
    val mediaQuery = window.matchMedia(COMPACT_BREAKPOINT_QUERY)
    var fullText = ""
    var compactText = ""

    fun renderCurrentBreakpoint() {
        chip.content = if (mediaQuery.matches) compactText else fullText
    }
    mediaQuery.addListener { renderCurrentBreakpoint() }

    fun load() {
        AppScope.launch {
            val result = api.usage()
            if (result is ApiResult.Success) {
                val usage = result.value
                fullText =
                    UsageFormat.full(
                        planId = usage.plan,
                        projectsUsed = usage.projectsUsed,
                        projectsLimit = usage.projectsLimit,
                        aiUsed = usage.aiGenerationsThisMonth,
                        aiLimit = usage.aiGenerationsLimit,
                        strings = t.usage,
                        format = t.format,
                    )
                compactText =
                    UsageFormat.compact(
                        projectsUsed = usage.projectsUsed,
                        projectsLimit = usage.projectsLimit,
                        aiUsed = usage.aiGenerationsThisMonth,
                        aiLimit = usage.aiGenerationsLimit,
                    )
                chip.setAttribute("title", fullText)
                if (UsageFormat.isAtAiLimit(usage.aiGenerationsThisMonth, usage.aiGenerationsLimit)) {
                    chip.addCssClass("kportal-usage-chip-limit")
                } else {
                    chip.removeCssClass("kportal-usage-chip-limit")
                }
                renderCurrentBreakpoint()
            }
        }
    }

    load()
    return UsageChip(chip) { load() }
}
